import { collection, query, orderBy, limit, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { subnetNames } from '../utils/subnetNames';
import { cacheService } from './cacheService';

export interface GainerData {
  netuid: number;
  name: string;
  value: number;
  percentChange: number;
  history?: Array<{
    timestamp: string;
    value: number;
  }>;
}

// Get top holder gainers in last 24h
export const getHolderGainers = async (limitCount = 3): Promise<GainerData[]> => {
  // Check cache first
  const cachedData = cacheService.getCache('holderGainers');
  if (cachedData) {
    return cachedData as GainerData[];
  }

  try {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    // Get all subnets first
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    const holderChanges: GainerData[] = [];
    
    // Process each subnet
    for (const netuid of subnets) {
      try {
        // Get entries from the last 24h for this subnet
        const entriesRef = collection(db, 'netuid_stats', netuid.toString(), 'entries');
        const entriesQuery = query(
          entriesRef,
          orderBy('timestamp', 'desc')
        );
        
        const entriesSnapshot = await getDocs(entriesQuery);
        if (entriesSnapshot.empty) continue;

        // Get all entries and sort by timestamp
        const entries = entriesSnapshot.docs
          .map(doc => {
            const data = doc.data();
            const timestamp = new Date(data.timestamp);
            
            // Calculate total holders from all categories
            const categories = data.categories || {};
            const totalHolders = Object.values(categories).reduce((sum: number, category: any) => {
              return sum + (category.count || 0);
            }, 0);
            
            return {
              timestamp,
              holders: totalHolders
            };
          })
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        if (entries.length >= 2) {
          // Get the most recent entry
          const currentEntry = entries[0];
          
          // Find entry closest to 24h ago
          const oneDayAgoTime = Date.now() - (24 * 60 * 60 * 1000);
          let previousEntry = entries[entries.length - 1];
          
          for (let i = 1; i < entries.length; i++) {
            const entry = entries[i];
            const timeDiff = Math.abs(entry.timestamp.getTime() - oneDayAgoTime);
            const currentDiff = Math.abs(previousEntry.timestamp.getTime() - oneDayAgoTime);
            
            if (timeDiff < currentDiff) {
              previousEntry = entry;
            }
          }

          // Calculate percentage change
          if (currentEntry.holders > 0 && previousEntry.holders > 0) {
            const percentChange = ((currentEntry.holders - previousEntry.holders) / previousEntry.holders) * 100;
            
            // Create history array for the chart
            const history = entries.map(entry => ({
              timestamp: entry.timestamp.toISOString(),
              value: entry.holders
            }));
            
            holderChanges.push({
              netuid,
              name: subnetNames[netuid] || `Subnet ${netuid}`,
              value: currentEntry.holders,
              percentChange,
              history
            });
          }
        }
      } catch (error) {
        console.error(`Error processing subnet ${netuid}:`, error);
        continue;
      }
    }
    
    // Sort by percent change (descending) and get top gainers
    const result = holderChanges
      .sort((a, b) => b.percentChange - a.percentChange)
      .filter(item => item.percentChange > 0) // Only include positive changes
      .slice(0, limitCount);
    
    // Cache the result
    cacheService.setCache('holderGainers', result);
    
    return result;
  } catch (error) {
    console.error('Error fetching holder gainers:', error);
    return [];
  }
};

// Get top Twitter score gainers in last 24h
export const getTwitterGainers = async (limitCount = 3): Promise<GainerData[]> => {
  // Check cache first
  const cachedData = cacheService.getCache('twitterGainers');
  if (cachedData) {
    return cachedData as GainerData[];
  }

  try {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    // Get current mindshare data
    const mindshareRef = collection(db, 'subnet_mindshare');
    const mindshareSnapshot = await getDocs(mindshareRef);
    
    const twitterChanges: GainerData[] = [];
    
    for (const doc of mindshareSnapshot.docs) {
      try {
        const netuid = parseInt(doc.id);
        const currentData = doc.data();
        
        // Get historical data
        const historyRef = collection(db, 'subnet_history', doc.id, 'entries');
        const historyQuery = query(
          historyRef,
          orderBy('timestamp', 'desc')
        );
        
        const historySnapshot = await getDocs(historyQuery);
        if (historySnapshot.empty) continue;
        
        // Get all entries and sort by timestamp
        const entries = historySnapshot.docs
          .map(doc => ({
            timestamp: new Date(doc.data().timestamp),
            score: doc.data().twitterEngagement || 0
          }))
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        if (entries.length >= 2) {
          const currentEntry = entries[0];
          
          // Find entry closest to 24h ago
          const oneDayAgoTime = Date.now() - (24 * 60 * 60 * 1000);
          let previousEntry = entries[entries.length - 1];
          
          for (let i = 1; i < entries.length; i++) {
            const entry = entries[i];
            const timeDiff = Math.abs(entry.timestamp.getTime() - oneDayAgoTime);
            const currentDiff = Math.abs(previousEntry.timestamp.getTime() - oneDayAgoTime);
            
            if (timeDiff < currentDiff) {
              previousEntry = entry;
            }
          }
          
          if (currentEntry.score > 0 && previousEntry.score > 0) {
            const percentChange = ((currentEntry.score - previousEntry.score) / previousEntry.score) * 100;
            
            // Create history array for the chart
            const history = entries.map(entry => ({
              timestamp: entry.timestamp.toISOString(),
              value: entry.score
            }));
            
            twitterChanges.push({
              netuid,
              name: subnetNames[netuid] || `Subnet ${netuid}`,
              value: currentEntry.score,
              percentChange,
              history
            });
          }
        }
      } catch (error) {
        console.error(`Error processing Twitter data for subnet ${doc.id}:`, error);
        continue;
      }
    }
    
    const result = twitterChanges
      .sort((a, b) => b.percentChange - a.percentChange)
      .filter(item => item.percentChange > 0) // Only include positive changes
      .slice(0, limitCount);
    
    // Cache the result
    cacheService.setCache('twitterGainers', result);
    
    return result;
  } catch (error) {
    console.error('Error fetching Twitter gainers:', error);
    return [];
  }
};

// Get top volume score gainers in last 24h
export const getVolumeGainers = async (limitCount = 3): Promise<GainerData[]> => {
  // Check cache first
  const cachedData = cacheService.getCache('volumeGainers');
  if (cachedData) {
    return cachedData as GainerData[];
  }

  try {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    // Get current mindshare data for volume scores
    const mindshareRef = collection(db, 'subnet_mindshare');
    const mindshareSnapshot = await getDocs(mindshareRef);
    
    const volumeChanges: GainerData[] = [];
    
    for (const doc of mindshareSnapshot.docs) {
      try {
        const netuid = parseInt(doc.id);
        const currentData = doc.data();
        
        // Get historical data
        const historyRef = collection(db, 'subnet_history', doc.id, 'entries');
        const historyQuery = query(
          historyRef,
          orderBy('timestamp', 'desc')
        );
        
        const historySnapshot = await getDocs(historyQuery);
        if (historySnapshot.empty) continue;
        
        // Get all entries and sort by timestamp
        const entries = historySnapshot.docs
          .map(doc => ({
            timestamp: new Date(doc.data().timestamp),
            score: doc.data().volumeScore || 0
          }))
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        if (entries.length >= 2) {
          const currentEntry = entries[0];
          
          // Find entry closest to 24h ago
          const oneDayAgoTime = Date.now() - (24 * 60 * 60 * 1000);
          let previousEntry = entries[entries.length - 1];
          
          for (let i = 1; i < entries.length; i++) {
            const entry = entries[i];
            const timeDiff = Math.abs(entry.timestamp.getTime() - oneDayAgoTime);
            const currentDiff = Math.abs(previousEntry.timestamp.getTime() - oneDayAgoTime);
            
            if (timeDiff < currentDiff) {
              previousEntry = entry;
            }
          }
          
          if (currentEntry.score > 0 && previousEntry.score > 0) {
            const percentChange = ((currentEntry.score - previousEntry.score) / previousEntry.score) * 100;
            
            // Create history array for the chart
            const history = entries.map(entry => ({
              timestamp: entry.timestamp.toISOString(),
              value: entry.score
            }));
            
            volumeChanges.push({
              netuid,
              name: subnetNames[netuid] || `Subnet ${netuid}`,
              value: currentEntry.score,
              percentChange,
              history
            });
          }
        }
      } catch (error) {
        console.error(`Error processing volume data for subnet ${doc.id}:`, error);
        continue;
      }
    }
    
    const result = volumeChanges
      .sort((a, b) => b.percentChange - a.percentChange)
      .filter(item => item.percentChange > 0) // Only include positive changes
      .slice(0, limitCount);
    
    // Cache the result
    cacheService.setCache('volumeGainers', result);
    
    return result;
  } catch (error) {
    console.error('Error fetching volume gainers:', error);
    return [];
  }
};

// Register refresh callbacks with the cache service
cacheService.registerRefreshCallback(async () => {
  await getHolderGainers();
});

cacheService.registerRefreshCallback(async () => {
  await getTwitterGainers();
});

cacheService.registerRefreshCallback(async () => {
  await getVolumeGainers();
});